// ===========================================
// Family credit hooks (#642)
// ===========================================

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { familyCreditApi } from '../api/familyCredit.api';
import type { FamilyCreditReportFilters, FamilyCreditSettings, FinancialYearSettings } from '../api/familyCredit.api';
import { invoiceKeys } from './useInvoices';

// Nested UNDER invoiceKeys.all (the familyPaymentKeys pattern): every invoice and payment
// mutation already invalidates invoiceKeys.all, so a payment recorded, voided or sent elsewhere
// refreshes the credit panel and the Refunds due card with no second wire-up. And every credit
// mutation below invalidates invoiceKeys.all, because it changes invoices too (Take off re-opens
// one; a Refund due switched back to credit comes off open invoices at once).
export const familyCreditKeys = {
  all: [...invoiceKeys.all, 'family-credit'] as const,
  family: (familyId: string) => [...familyCreditKeys.all, familyId] as const,
  refundsDue: () => [...familyCreditKeys.all, 'refunds-due'] as const,
  settings: () => [...familyCreditKeys.all, 'settings'] as const,
  financialYear: () => [...familyCreditKeys.all, 'financial-year'] as const,
  report: (filters: FamilyCreditReportFilters) => [...familyCreditKeys.all, 'report', filters] as const,
};

export function useFamilyCredit(familyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: familyCreditKeys.family(familyId ?? ''),
    queryFn: () => familyCreditApi.get(familyId as string),
    enabled: enabled && !!familyId,
  });
}

/** The dashboard card. Pass `enabled: can('operations_refunds', 'write')` (#272). */
export function useRefundsDue(enabled: boolean) {
  return useQuery({
    queryKey: familyCreditKeys.refundsDue(),
    queryFn: () => familyCreditApi.refundsDue(),
    enabled,
  });
}

/**
 * The credit actions. Errors are NOT snackbarred: each dialog shows the server's refusal itself
 * (it names what to undo first).
 */
function useCreditMutation<V>(fn: (v: V) => Promise<unknown>, success: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      enqueueSnackbar(success, { variant: 'success' });
    },
  });
}

export function useTakeOffCredit() {
  return useCreditMutation(
    ({ entryId, reason }: { entryId: string; reason: string }) => familyCreditApi.takeOff(entryId, reason),
    'Account credit taken off the invoice'
  );
}

export function useFlagRefundDue() {
  return useCreditMutation(
    ({ familyId, amount, note }: { familyId: string; amount: number; note: string }) =>
      familyCreditApi.flagRefundDue(familyId, amount, note),
    'Marked as refund due'
  );
}

export function useMarkRefunded() {
  return useCreditMutation(
    ({ entryId, refundedOn, reference, note }: { entryId: string; refundedOn: string; reference: string; note?: string }) =>
      familyCreditApi.markRefunded(entryId, { refundedOn, reference, note }),
    'Refund recorded'
  );
}

export function useVoidRefundEntry() {
  return useCreditMutation(
    ({ entryId, reason }: { entryId: string; reason: string }) => familyCreditApi.voidRefundEntry(entryId, reason),
    'Refund record voided'
  );
}

// ---- #642 Phase 2: the settings (owner changes them) and the family credit report (owner only) ----

export function useFamilyCreditSettings(enabled: boolean) {
  return useQuery({ queryKey: familyCreditKeys.settings(), queryFn: () => familyCreditApi.getSettings(), enabled });
}

export function useFinancialYearSettings(enabled: boolean) {
  return useQuery({ queryKey: familyCreditKeys.financialYear(), queryFn: () => familyCreditApi.getFinancialYear(), enabled });
}

/** Owner only. Saving either setting refreshes every family credit read (the report reads both). */
export function useUpdateFamilyCreditSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async ({ credit, financialYear }: { credit?: Partial<FamilyCreditSettings>; financialYear?: FinancialYearSettings }) => {
      if (credit && Object.keys(credit).length > 0) await familyCreditApi.updateSettings(credit);
      if (financialYear) await familyCreditApi.updateFinancialYear(financialYear);
    },
    onSuccess: () => enqueueSnackbar('Account credit settings saved', { variant: 'success' }),
    // Settled, not success: when the first PUT lands and the second fails, the page must still
    // show what was saved (/qa).
    onSettled: () => queryClient.invalidateQueries({ queryKey: familyCreditKeys.all }),
  });
}

export function useFamilyCreditReport(filters: FamilyCreditReportFilters, enabled: boolean) {
  return useQuery({
    queryKey: familyCreditKeys.report(filters),
    queryFn: () => familyCreditApi.report(filters),
    enabled,
    // Keep the last report on screen while another period loads (the year menu and the period
    // label read from it).
    placeholderData: keepPreviousData,
  });
}

/** The CSV download (same filters, same gate). */
export function useExportFamilyCreditReport() {
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (filters: FamilyCreditReportFilters) => familyCreditApi.exportReport(filters),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `family-credit-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () => enqueueSnackbar('Could not download the family credit report. Please try again.', { variant: 'error' }),
  });
}
